/* ErasRoad — "Career roadmap": a winding-road timeline of every role.
   Production port of the prototype's roadmap.jsx (V6 mock-up, authoritative).

   Every geometry constant below is BAKED from Srinath's final placement
   (Copy-layout export, 2026-06-09) — copied verbatim, never re-derived.
   The design-time authoring layer (drag, road editing, bend/rotate handles,
   color swatches, resize grips, Copy/Reset layout, localStorage persistence)
   is intentionally omitted: this is the static layout every visitor sees. */

import { Fragment, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { BrandLogo } from "../lib/brands";
import { expsByRecency } from "../lib/queries";

/* ── Fixed canvas (sized to fill the panel near 1:1 → readable type) ─────── */
const RM_W = 1180;
const RM_H = 1200;

/* Canvas stretch + pan — Srinath's final (Copy-layout export, 2026-06-09). */
const RM_DEF_SIZE = { sx: 0.852, sy: 0.893 };
const RM_DEF_PAN = { x: 7, y: -8 };

/* Career sequence, present → past — the order the road threads through. */
const ROAD_ORDER = [
  "deloitte", "cisco", "sj-enterprise", "metlife", "bcbs-nc", "bofa-merrill",
  "ibm-gs", "verizon-business", "ibm-sg", "ibm-pcd", "startec", "ibm-tvm", "cybernet"];

/* ── Per-card accent. Per Srinath: bright, primary-ish colors repeated in a
   fixed sequence. No data meaning — the color simply flips the eye's micro-
   context as it steps from one card to the next. Ordered warm/cool/warm/cool so
   the three warms (red·orange·gold) never touch and blue never sits by violet. */
const ROAD_SEQ = ["#e5484d", "#41a85f", "#f2820c", "#2f6fe0", "#d9a400", "#8e4ec6"];
const ROAD_HUE: Record<string, string> = {};
ROAD_ORDER.forEach((id, i) => { ROAD_HUE[id] = ROAD_SEQ[i % ROAD_SEQ.length]; });

/* ── Curated card copy — transcribed verbatim from the V6 mock-up. ───────── */
interface RoadCardMeta {
  role: string;
  years: string;
  now?: boolean;
  focus: string[];
}
const ROAD_CARDS: Record<string, RoadCardMeta> = {
  "deloitte": { role: "Enterprise Architect", years: "2021 – Now", now: true, focus: ["AI Adoption & Operations", "Cloud Contact Centers"] },
  "cisco": { role: "Program Manager", years: "2020 – 2020", focus: ["Software Conformance", "‘Ask the Experts’ Webinar"] },
  "sj-enterprise": { role: "Chief Architect", years: "2018 – 2020", focus: ["Zoho Partner", "Multi-tenant CCaaS"] },
  "metlife": { role: "Director", years: "2015 – 2018", focus: ["Marketing Automation", "Vendor Management"] },
  "bcbs-nc": { role: "Program Manager", years: "2014 – 2015", focus: ["Customer Intelligence", "Marketing Analytics"] },
  "bofa-merrill": { role: "Delivery Manager", years: "2010 – 2014", focus: ["Navigator FSI CRM", "Party Onboarding"] },
  "ibm-gs": { role: "Solutions Architect", years: "2006 – 2009", focus: ["Airport Operations & IS", "Enterprise Asset Management"] },
  "verizon-business": { role: "Integration Architect", years: "2005 – 2006", focus: ["Customer Service Portal", "Employee Service Portal"] },
  "ibm-sg": { role: "Application Architect", years: "2004 – 2005", focus: ["Custom Event Bus", "Performance Tuning"] },
  "ibm-pcd": { role: "Team Lead", years: "2000 – 2004", focus: ["B2B Account Management", "Web Enablement"] },
  "startec": { role: "Application Developer", years: "1999 – 2000", focus: ["Intranet Applications", "Database Integrations"] },
  "ibm-tvm": { role: "Programmer", years: "1997 – 1999", focus: ["College Application Portal", "Online Reservation System"] },
  "cybernet": { role: "Programmer", years: "1996 – 1997", focus: ["Web-based Auction Portal", "Groupware Implementation"] }
};

/* Display names with baked abbreviations — single line, never wrap/truncate. */
const ROAD_NAME: Record<string, string> = {
  "deloitte": "Deloitte Consulting", "cisco": "Cisco Systems", "sj-enterprise": "SJ Enterprise Solutions",
  "metlife": "MetLife Inc.", "bcbs-nc": "Blue Cross Blue Shield NC", "bofa-merrill": "Bank of America ML",
  "ibm-gs": "IBM Global Services", "verizon-business": "Verizon Business", "ibm-sg": "IBM Software Group",
  "ibm-pcd": "IBM PCD", "startec": "Startec Global Comm.", "ibm-tvm": "IBM TV & Media ISU",
  "cybernet": "Cybernet Software"
};

/* ── Geometry, baked from Srinath's final placement (Copy-layout export,
   2026-06-09). card = [x,y] top-left. flag = [x,y] road contact point.
   conn = [dx,dy] fine-tune offset Srinath dragged onto a card's connector. ── */
type Pt = [number, number];
interface RoadNodeGeom {
  flag: Pt;
  card: Pt;
  conn?: Pt;
}
const ROAD_NODES: Record<string, RoadNodeGeom> = {
  "deloitte": { flag: [20, 227], card: [-12, 107] },
  "cisco": { flag: [325, 256], card: [344, 137] },
  "sj-enterprise": { flag: [618, 302], card: [632, 168], conn: [0, 24] },
  "metlife": { flag: [1016, 478], card: [980, 352], conn: [-1, -1] },
  "bcbs-nc": { flag: [653, 551], card: [518, 398] },
  "bofa-merrill": { flag: [365, 517], card: [225, 384] },
  "ibm-gs": { flag: [66, 590], card: [-66, 445], conn: [89, 8] },
  "verizon-business": { flag: [41, 779], card: [9, 659], conn: [57, 1] },
  "ibm-sg": { flag: [343, 741], card: [295, 602] },
  "ibm-pcd": { flag: [620, 718], card: [604, 611], conn: [16, 10] },
  "startec": { flag: [876, 842], card: [804, 748], conn: [0, 24] },
  "ibm-tvm": { flag: [568, 955], card: [497, 811], conn: [42, -9] },
  "cybernet": { flag: [247, 1000], card: [182, 859] }
};

/* The road threads the flag points (under each card's logo) in career order,
   with a short tangential stub at each end so it enters/leaves cleanly. */
function rmExtend(a: Pt, b: Pt, t: number): Pt {
  return [Math.round(a[0] + (a[0] - b[0]) * t), Math.round(a[1] + (a[1] - b[1]) * t)];
}
const ROAD_FLAG_PTS: Pt[] = ROAD_ORDER.map((id) => ROAD_NODES[id].flag);
const ROAD_PTS: Pt[] = [
  rmExtend(ROAD_FLAG_PTS[0], ROAD_FLAG_PTS[1], 0.12),
  ...ROAD_FLAG_PTS,
  rmExtend(ROAD_FLAG_PTS[ROAD_FLAG_PTS.length - 1], ROAD_FLAG_PTS[ROAD_FLAG_PTS.length - 2], 0.12)];

/* Is the road running mostly vertically at this node? Only skip the connector
   when BOTH adjacent road segments are vertical — at a hairpin apex (MetLife)
   the road sweeps in/out horizontally, so the line reads fine and stays. */
function roadVerticalAt(id: string): boolean {
  const i = ROAD_ORDER.indexOf(id);
  const cur = ROAD_FLAG_PTS[i];
  const prev = ROAD_FLAG_PTS[i - 1] || cur;
  const next = ROAD_FLAG_PTS[i + 1] || cur;
  const v1 = Math.abs(cur[1] - prev[1]) > Math.abs(cur[0] - prev[0]);
  const v2 = Math.abs(next[1] - cur[1]) > Math.abs(next[0] - cur[0]);
  return v1 && v2;
}

/* Decade markers — short form, large italic; positions are Srinath's final. */
interface RoadDecade {
  t: string;
  x: number;
  y: number;
  op: number;
}
const ROAD_DECADES: RoadDecade[] = [
  { t: "’20s", x: 923, y: 239, op: 0.82 },
  { t: "’10s", x: 37, y: 364, op: 0.62 },
  { t: "’00s", x: 874, y: 619, op: 0.48 },
  { t: "’90s", x: 44, y: 935, op: 0.36 }];

/* Era highlights — the author's observations, curved to ride the road. All
   positions, bends (c1/c2 lift the left/right thirds), and rotations are
   Srinath's final authored values (Copy-layout export, 2026-06-09).
   Wording is the author's own — never modify it. */
interface RoadNote {
  t: string;
  x: number;
  y: number;
  c1?: number;
  c2?: number;
  rot?: number;
  fs?: number;
  accent?: boolean;
  pin?: boolean;
  star?: Pt;
}
const ROAD_NOTES: RoadNote[] = [
  { t: "AI redefines “work”", x: 11, y: 189, c1: 6, c2: 7, rot: 6, fs: 20, accent: true },
  { t: "Workforces go remote", x: 292, y: 259, c1: 12, c2: 5, rot: 4, fs: 18 },
  { t: "Cloud adoption gains traction", x: 689, y: 318, c1: 36, c2: -13, rot: 17, fs: 18 },
  { t: "Amazon Connect debuts (2017)", x: 658, y: 251, fs: 17, pin: true, star: [741, 345] },
  { t: "Containers share virtualized resources", x: 523, y: 481, c1: -34, c2: 19, rot: -1, fs: 17 },
  { t: "Microservices separate concerns", x: 15, y: 500, c1: 67, c2: 12, rot: -19, fs: 18 },
  { t: "n-Tier architecture for distributed workloads", x: 139, y: 714, c1: 30, c2: -16, rot: -12, fs: 17 },
  { t: "E-commerce becomes viable", x: 613, y: 898, c1: -17, c2: -9, rot: -17, fs: 18 },
  { t: "Groupware enables collaborative business", x: 167, y: 937, c1: 0, c2: -21, rot: -9, fs: 17 }];

/* Default text colors — Srinath's picks (set via direct edit, 2026-06-09):
   decades + pinned notes in deep teal, curved era highlights in violet. */
const RM_LABEL_DEFAULT = "rgb(33, 120, 110)";
const RM_ERA_DEFAULT = "rgb(162, 50, 193)";

/* smooth wavy road through the points (Catmull-Rom → cubic bezier) */
function rmRoundedPath(pts: Pt[]): string {
  if (pts.length < 2) return "";
  const d = [`M ${pts[0][0]} ${pts[0][1]}`];
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i], p1 = pts[i], p2 = pts[i + 1], p3 = pts[i + 2] || pts[i + 1];
    const cp1x = p1[0] + (p2[0] - p0[0]) / 6, cp1y = p1[1] + (p2[1] - p0[1]) / 6;
    const cp2x = p2[0] - (p3[0] - p1[0]) / 6, cp2y = p2[1] - (p3[1] - p1[1]) / 6;
    d.push(`C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)} ${cp2x.toFixed(1)} ${cp2y.toFixed(1)} ${p2[0]} ${p2[1]}`);
  }
  return d.join(" ");
}

/* Everything below is static — path + connector skips computed once. */
const ROAD_PATH = rmRoundedPath(ROAD_PTS);
const SKIP_CONNECTOR = new Set(ROAD_ORDER.filter(roadVerticalAt));

/* ── Rich employer card ─────────────────────────────────────────────────── */
function RoadCard({ id }: { id: string }) {
  const c = ROAD_CARDS[id];
  const [x, y] = ROAD_NODES[id].card;
  const hue = ROAD_HUE[id] || "var(--accent)";
  if (!c) return null;
  return (
    <Link to={`/experience/${id}`} className="rm-empcard"
      style={{ left: x, top: y, "--rm-hue": hue, zIndex: 2 } as CSSProperties}
      title={`${ROAD_NAME[id]} — ${c.role}`}>
      <span className="rm-empcard-edge" />
      <span className="rm-empcard-main">
        <span className="co-logo" style={{ width: 40, height: 40, borderRadius: 9, padding: 3 }} title={ROAD_NAME[id]}>
          <BrandLogo company={id} size={40} bare />
        </span>
        <span className="rm-empcard-sep" />
        <span className="rm-empcard-info">
          <span className="rm-empcard-co">{ROAD_NAME[id]}</span>
          <span className="rm-empcard-role">{c.role}</span>
          <span className="rm-empcard-years mono">
            {c.years}{c.now && <span className="rm-empcard-now">Now</span>}
          </span>
        </span>
      </span>
      <span className="rm-empcard-focus">
        <span className="rm-empcard-focus-inner">
          {c.focus.map((f, i) =>
            <span className="rm-empcard-focusitem" key={i}><span className="rm-empcard-dot" />{f}</span>
          )}
        </span>
      </span>
    </Link>);
}

/* ── Curved single-line text (era highlights ride the road's undulation) ───
   Two control points → an S-curve, so a note can match a road that bends both
   ways. c1/c2 lift the left/right thirds; rotation lives on the wrapper. ── */
function CurvedText({ id, text, c1 = 0, c2 = 0, fontSize = 18 }: {
  id: string;
  text: string;
  c1?: number;
  c2?: number;
  fontSize?: number;
}) {
  const w = Math.max(64, Math.round(text.length * fontSize * 0.6) + 26);
  const up = Math.max(0, c1, c2), down = Math.max(0, -c1, -c2);
  const h = up + down + fontSize + 26;
  const baseY = up + fontSize + 8;
  const C1: Pt = [w / 3, baseY - c1], C2: Pt = [(2 * w) / 3, baseY - c2];
  const d = `M 8 ${baseY} C ${C1[0]} ${C1[1]} ${C2[0]} ${C2[1]} ${w - 8} ${baseY}`;
  return (
    <svg className="rm-era-svg" width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-label={text} style={{ overflow: "visible" }}>
      <path id={id} d={d} fill="none" />
      <text style={{ fontSize, fontWeight: 400, fill: RM_ERA_DEFAULT }}>
        <textPath href={`#${id}`} xlinkHref={`#${id}`} startOffset="50%" textAnchor="middle">{text}</textPath>
      </text>
    </svg>);
}

/* ── Panel ──────────────────────────────────────────────────────────────── */
export function ErasRoad() {
  /* Card order derives from the data at render time; geometry stays baked. */
  const order = useMemo(
    () => expsByRecency().map((e) => e.id).filter((id) => id in ROAD_NODES),
    []);

  /* Responsive fit: scale the fixed canvas down to the frame width (never up). */
  const wrapRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [frameW, setFrameW] = useState(0);
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.getBoundingClientRect().width;
      setScale(Math.min(1, w / (RM_W * RM_DEF_SIZE.sx)));
      setFrameW(w);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="careerroad-panel">
      <div className="careerroad-head">
        <p className="section-sub" style={{ margin: 0, textAlign: "center" }}>
          <i>Every hairpin bend, a decade goes by.</i><br />
          <i>Moving forward with purpose; looking back with pride.</i>
        </p>
      </div>

      <div className="careerroad-scaleframe" ref={wrapRef} style={{ height: RM_H * RM_DEF_SIZE.sy * scale }}>
        <div className="careerroad-canvas"
          style={{
            width: RM_W,
            height: RM_H,
            cursor: "default", /* neutralise the stylesheet's design-time pan cursor */
            transform: `translate(${Math.max(0, (frameW - RM_W * RM_DEF_SIZE.sx * scale) / 2) + RM_DEF_PAN.x * scale}px, ${RM_DEF_PAN.y * scale}px) scale(${scale * RM_DEF_SIZE.sx}, ${scale * RM_DEF_SIZE.sy})`
          }}>
          <svg className="rm-road-svg" width={RM_W} height={RM_H} viewBox={`0 0 ${RM_W} ${RM_H}`} aria-hidden="true">
            <path d={ROAD_PATH} className="rm-road-base" fill="none" strokeLinecap="butt" strokeLinejoin="round" />
            <path d={ROAD_PATH} className="rm-road-line" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            {order.map((id) => {
              if (SKIP_CONNECTOR.has(id)) return null; // skip where a vertical line would hide in the road
              // line hangs beneath the card's logo and ends at the road's centerline
              const def = ROAD_NODES[id].conn || [0, 0];
              const [cx2, cy2] = ROAD_NODES[id].card;
              const x = cx2 + 32 + def[0];
              const y1 = cy2 + 50; // inside the card → visible from its bottom edge
              const y2 = ROAD_NODES[id].flag[1] + def[1];
              return <line key={"conn:" + id} className="rm-connector" x1={x} y1={y1} x2={x} y2={y2} />;
            })}
            {ROAD_NOTES.map((n, i) => {
              if (!n.pin || !n.star) return null;
              return <line key={"starline:" + i} className="rm-star-line"
                x1={n.x + 76} y1={n.y + 50} x2={n.star[0]} y2={n.star[1] - 10} />;
            })}
          </svg>

          {ROAD_DECADES.map((d, i) =>
            <span key={"dec:" + i} className="rm-decade2"
              style={{ left: d.x, top: d.y, opacity: d.op, zIndex: 1 }}>
              <span style={{ fontWeight: 400, color: RM_LABEL_DEFAULT }}>{d.t}</span>
            </span>
          )}

          {ROAD_NOTES.map((n, i) => {
            if (n.pin && n.star) {
              return (
                <Fragment key={"era:" + i}>
                  <span className="rm-pinnote" style={{ left: n.x, top: n.y, zIndex: 2 }}>
                    <span style={{ display: "block", fontWeight: 400, color: RM_LABEL_DEFAULT }}>{n.t}</span>
                  </span>
                  <span className="rm-star" style={{ left: n.star[0], top: n.star[1], zIndex: 4 }}>
                    <Star size={22} />
                  </span>
                </Fragment>);
            }
            const rot = n.rot || 0;
            return (
              <span key={"era:" + i} className={"rm-era2" + (n.accent ? " is-accent" : "")}
                style={{
                  position: "absolute", left: n.x, top: n.y, zIndex: 1,
                  transform: `rotate(${rot}deg)`, transformOrigin: "center"
                }}>
                <CurvedText id={`era-curve-${i}`} text={n.t} c1={n.c1} c2={n.c2} fontSize={n.fs || 18} />
              </span>);
          })}

          {order.map((id) => <RoadCard key={"card:" + id} id={id} />)}
        </div>
      </div>
    </div>);
}
